import { Injectable } from "@angular/core";
import { HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { tap } from "rxjs/operators";
import { HttpClientService } from "./http-client.service";
import {
  EpConversation,
  EpDashboardResponse,
  EpLeadTab,
  EpUnarchiveResponse,
  EpWhatsappLinkResponse,
  EpWhatsappRecipient,
} from "../interfaces/ep-leads/ep-lead.interface";

/** Pagination options for the dashboard request. */
export interface EpDashboardPagination {
  page?: number;
  perPage?: number;
  /** Aterriza en la página que contiene este lead (búsqueda / ubicación). */
  locateId?: number;
  /** Filtro server-side por nombre de agencia (todas las páginas). */
  agency?: string;
  /** Búsqueda server-side (nombre / id / application_id) sobre el tab completo. */
  search?: string;
}

@Injectable({ providedIn: "root" })
export class EpDashboardService {
  constructor(private readonly http: HttpClientService) {}

  /**
   * EP leads dashboard (cero-fuga portfolio) for the authenticated EP (current_resource_owner).
   * GET /api/ep/dashboard?tab=with_bot|to_contact|incomplete|to_send_to_analysis|approved|archived&page=&per_page=
   * `epId` is only sent by developers to inspect another EP's portfolio (backend authorizes it).
   */
  getDashboard(
    tab: EpLeadTab,
    epId?: string | number,
    pagination?: EpDashboardPagination
  ): Observable<EpDashboardResponse> {
    let params = new HttpParams().set("tab", tab);
    if (epId !== undefined && epId !== null && String(epId).trim() !== "") {
      params = params.set("ep_id", String(epId));
    }
    if (pagination?.page != null) {
      params = params.set("page", String(pagination.page));
    }
    if (pagination?.perPage != null) {
      params = params.set("per_page", String(pagination.perPage));
    }
    if (pagination?.locateId != null) {
      params = params.set("locate_id", String(pagination.locateId));
    }
    if (pagination?.agency) {
      params = params.set("agency", pagination.agency);
    }
    if (pagination?.search) {
      params = params.set("q", pagination.search);
    }
    return this.http
      .get<EpDashboardResponse>("/api/ep/dashboard", { params })
      .pipe(tap(() => console.log("EpDashboardService.getDashboard: Ok.")));
  }

  /**
   * WhatsApp deep link with a prefilled message for a lead.
   * GET /api/ep/leads/:cardId/whatsapp_link?recipient=client|salesman
   * `cardId` is the card id (ExternalQuote.id); `recipient` chooses whom to contact (defaults to `client`).
   */
  getWhatsappLink(cardId: number, recipient?: EpWhatsappRecipient): Observable<EpWhatsappLinkResponse> {
    let params = new HttpParams();
    if (recipient) {
      params = params.set("recipient", recipient);
    }
    return this.http
      .get<EpWhatsappLinkResponse>(`/api/ep/leads/${cardId}/whatsapp_link`, { params })
      .pipe(tap(() => console.log("EpDashboardService.getWhatsappLink: Ok.")));
  }

  /**
   * Archives a lead so it leaves the active portfolio (it stays retrievable from the archived panel).
   * POST /api/ep/leads/:cardId/archive  (cardId = ExternalQuote.id)
   */
  archiveLead(cardId: number): Observable<void> {
    return this.http
      .post(`/api/ep/leads/${cardId}/archive`, {})
      .pipe(tap(() => console.log("EpDashboardService.archiveLead: Ok.")));
  }

  /**
   * Restores an archived lead back into the active portfolio.
   * POST /api/ep/leads/:cardId/unarchive  (cardId = ExternalQuote.id)
   * Returns the destination `tab` so the UI can deep-link to the restored lead.
   */
  unarchiveLead(cardId: number): Observable<EpUnarchiveResponse> {
    return this.http
      .post(`/api/ep/leads/${cardId}/unarchive`, {})
      .pipe(tap(() => console.log("EpDashboardService.unarchiveLead: Ok.")));
  }

  /**
   * Bot conversation for the EP "observe" panel. Rails reads Chatwoot
   * server-side and returns the normalized messages.
   * GET /api/ep/leads/:cardId/conversation  (cardId = ExternalQuote.id)
   */
  getConversation(cardId: number): Observable<EpConversation> {
    return this.http
      .get<EpConversation>(`/api/ep/leads/${cardId}/conversation`)
      .pipe(tap(() => console.log("EpDashboardService.getConversation: Ok.")));
  }
}
